import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { Box, Grid2 as Grid, IconButton, Typography } from "@mui/material";
import { useRecordContext } from "react-admin";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";
import { ValidAny } from "@/types";
import { useAppTheme } from "@/hooks/useAppTheme";

export type StatCardConfig = {
  label: string;
  value?: string | number;
  component?: React.ReactNode;
  color?: "primary" | "secondary" | "success" | "error" | "info" | "warning";
  borderAccent?: boolean;
  formatCurrency?: boolean;
  accessor?: (record: ValidAny) => string | number;
  link?: string;
  tooltip?: string;
};

interface StatCardGridProps {
  cards: StatCardConfig[];
  variant?: "compact" | "regular";
}

export const StatCardGrid = ({ cards, variant = "regular" }: StatCardGridProps) => {
  const { theme, isDarkMode } = useAppTheme();
  const record = useRecordContext();
  const cardBgColor = isDarkMode ? theme.palette.grey[900] : theme.palette.grey[100];
  const cardBorderColor = theme.palette.divider;

  const isCompact = variant === "compact";
  const columnSize = cards.length <= 2 ? 6 : cards.length <= 4 ? 4 : cards.length <= 6 ? 3 : 2;

  return (
    <Grid container spacing={1.5} pt={1} pb={1}>
      {cards.map((card, idx) => {
        const rawValue =
          card.component ??
          (card.formatCurrency
            ? formatUSD(Number(card.accessor?.(record) ?? card.value ?? 0))
            : (card.accessor?.(record) ?? card.value ?? "-"));

        const value =
          typeof rawValue === "string" || typeof rawValue === "number" ? (
            <Typography
              variant={isCompact ? "subtitle1" : "h6"}
              fontWeight={card.borderAccent ? 600 : "default"}
              color={card.color ? `${card.color}.main` : "text.primary"}
              noWrap
            >
              {rawValue}
            </Typography>
          ) : (
            rawValue
          );

        return (
          <Grid
            key={idx}
            size={{
              xs: 12,
              sm: 6,
              md: 4,
              lg: columnSize,
            }}
          >
            <Box
              height="100%"
              p={isCompact ? 2 : 3}
              borderRadius={1.5}
              bgcolor={cardBgColor}
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              border="1px solid"
              borderColor={cardBorderColor}
              sx={
                card.borderAccent
                  ? {
                      borderLeft: `10px solid ${theme.palette[card.color || "secondary"].main}`,
                    }
                  : {}
              }
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  {card.label}
                </Typography>
                {card.link && (
                  <Link to={card.link}>
                    <IconButton size="small" sx={{ p: 0.5 }}>
                      <OpenInNewIcon fontSize="inherit" />
                    </IconButton>
                  </Link>
                )}
              </Box>
              {value}
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};
